import { FormButtonBox } from "@/features/standard/form/FormButtonBox";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormSelect } from "@/features/standard/form/FormSelect";
import { FormWrapper } from "@/features/standard/form/FormWrapper";
import { Button, TextInput } from "@mantine/core";

import { mutateVehicles } from "@/api/vehicle/useVehicleQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/Alert";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import { useForm } from "@mantine/form";
import { ScmApiVehiclesPostRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const Create = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Select: FormSelect,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
});

interface LocationCreateFormProps {
  code?: string;
  autoCompleteOnSubmit?: (values: any) => void;
}

export const VehicleCreateForm = (params: LocationCreateFormProps) => {
  const { code, autoCompleteOnSubmit } = params;
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate: createMutate } = useMutation(
    (params: ScmApiVehiclesPostRequest) =>
      mutateVehicles.create(params).mutationFn(params as ScmApiVehiclesPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["vehicles"]);
        closeModal(form.values);
        customAlert(
          t("message.createSuccess", { item: t("vehicle.vehicle") }),
          t("common.createSuccess"),
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("vehicle.code") }),
          t("common.createFail", { item: t("vehicle.vehicle") }),
          "red",
        );
      },
    },
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>("");

  const form = useForm({
    initialValues: {
      code: code ?? undefined,
      name: undefined,
      plateNumber: undefined,
      driver: undefined,
      description: undefined,
    },
  });

  const onSubmit = async () => {
    try {
      createMutate({
        vehiclesGetRequest: {
          code: code ?? form.values.code!,
          name: form.values.name!,
          plateNumber: form.values.plateNumber,
          driver: form.values.driver,
          description: form.values.description,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("vehicle.code") }),
        t("common.createFail", { item: t("vehicle.vehicle") }),
        "red",
      );
    }
  };

  return (
    <>
      <Create.Wrapper>
        <Create.Input
          label={t("vehicle.code")}
          placeholder={t("placeholder.input", { item: t("vehicle.code") })}
          withAsterisk
          {...form.getInputProps("code")}
        />
        <Create.Input
          label={t("vehicle.name")}
          placeholder={t("placeholder.input", { item: t("vehicle.name") })}
          {...form.getInputProps("name")}
        />
        <Create.Input
          width="100%"
          label={t("vehicle.plateNumber")}
          placeholder={t("placeholder.input", { item: t("site.code") })}
          {...form.getInputProps("plateNumber")}
        />
        <Create.Input
          label={t("vehicle.driver")}
          placeholder={t("placeholder.input", { item: t("vehicle.driver") })}
          {...form.getInputProps("driver")}
        />
        <Create.Input
          label={t("vehicle.description")}
          placeholder={t("placeholder.input", { item: t("vehicle.description") })}
          {...form.getInputProps("description")}
        />
        <Create.ButtonBox>
          <Create.Button color="gray" onClick={closeModal}>
            {t("common.cancel")}
          </Create.Button>
          <Create.Button
            disabled={validateFormValues({
              pageType: "vehicle",
              values: form.values,
            })}
            onClick={autoCompleteOnSubmit ? () => autoCompleteOnSubmit(form.values) : onSubmit}
          >
            {t("common.create", { item: t("vehicle.vehicle") })}
          </Create.Button>
        </Create.ButtonBox>
      </Create.Wrapper>
    </>
  );
};
